import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useApiStore } from '@core/store';
import { NewPageTitleBar, LoadingSpinner } from '@/components';
import ShopSelector from '@/components/selectors/ShopSelector';
import AllHealthsSelector from '@/components/selectors/AllHealthsSelector';
import TrolleyShelterMonitoringCard from './components/TrolleyShelter/TrolleyShelterMonitoringCard';
import { apiClient } from '@core/api';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';

interface Threshold {
    value: number;
    thresholdType: number;
    productId?: string;
    productCapacity?: number;
}

interface NotificationRule {
    type: number;
    thresholds: Threshold[];
}

interface Shelter {
    id: string;
    moduleId: string;
    capacity: number;
    disabled: boolean;
    deleteDate: string | null;
    description?: string;
    imagePath?: string;
    notificationRule: NotificationRule;
}

interface FillLevel {
    shelterId: string;
    productId?: string;
    fillLevel: number;
}

interface ShelterProduct {
    name: string;
    requiredAction: string;
}

const ThresholdType = Object.freeze({
    Upper: 0,
    Lower: 1
});

const healthStates = {
    good: {
        id: 'good',
        value: 'goodHealth',
        icon: 'zmdi-info',
        color: 'success'
    },
    warn: {
        id: 'warn',
        value: 'warnHealth',
        icon: 'zmdi-alert-triangle',
        color: 'warning'
    },
    bad: {
        id: 'bad',
        value: 'badHealth',
        icon: 'zmdi-alert-triangle',
        color: 'error'
    }
};

const types = {
    single: { id: 0, text: 'single' },
    perType: { id: 1, text: 'perType' }
};

const reloadInterval = 5 * 60 * 1000;

const TrolleyShelterMonitoring: FCSecurePage = () => {
    const { t } = useTranslation();
    const { rfidTagProducts, loadRfidTagProducts } = useApiStore();

    const [loading, setLoading] = useState(false);
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [selectedHealths, setSelectedHealths] = useState<string[]>([healthStates.bad.id]);
    const [allShelters, setAllShelters] = useState<Shelter[]>([]);
    const [fillLevels, setFillLevels] = useState<FillLevel[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        loadRfidTagProducts();
    }, []);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedShopIds[0]) {
            loadShelters();
        } else {
            // Reset state when no shop is selected
            setAllShelters([]);
            setFillLevels([]);
        }
    }, [selectedShopIds[0]]);

    const loadShelters = useCallback(async () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (!selectedShopIds[0]) return;

        setLoading(true);

        try {
            await Promise.all([
                getTrolleyShelters(),
                getFillLevels().catch((err) => {
                    console.warn('[TrolleyShelterMonitoring] Fill levels failed, continuing anyway:', err);
                    setFillLevels([]);
                })
            ]);
        } finally {
            setLoading(false);
            timeoutRef.current = setTimeout(() => {
                loadShelters();
            }, reloadInterval);
        }
    }, [selectedShopIds[0]]);

    useRegisterRefresh(loadShelters, !selectedShopIds[0]);

    const getTrolleyShelters = async () => {
        try {
            const response = await apiClient.get<Shelter[]>(`trolleyshelter/${selectedShopIds[0]}`);

            // The API returns modules directly, filter out disabled/deleted ones
            const filteredShelters = response.data.filter((shelter) => !shelter.disabled && !shelter.deleteDate);
            setAllShelters(filteredShelters);
        } catch (error) {
            console.error('[TrolleyShelterMonitoring] Error fetching trolley shelters:', error);
            setAllShelters([]); // Set empty array on error
        } finally {
            setLastUpdate(DateTime.now());
        }
    };

    const getFillLevels = async () => {
        try {
            const response = await apiClient.get<FillLevel[]>(
                `statistics/shelterfilllevel/${selectedShopIds[0]}/products`
            );
            setFillLevels(response.data || []); // Ensure we always set an array
        } catch (error) {
            console.error('[TrolleyShelterMonitoring] Error fetching fill levels:', error);
            // Set empty array on error to prevent blocking
            setFillLevels([]);
        }
    };

    const getShelterProducts = useCallback(
        (shelter: Shelter): ShelterProduct[] => {
            const shelterProducts: ShelterProduct[] = [];

            if (shelter.notificationRule.type !== types.perType.id) {
                return shelterProducts;
            }

            const distinctShelterProducts = [...new Set(shelter.notificationRule.thresholds.map((x) => x.productId))];

            distinctShelterProducts.forEach((productId) => {
                if (!productId) return;

                const productThresholds = shelter.notificationRule.thresholds.filter(
                    (threshold) => threshold.productId === productId
                );

                const name = rfidTagProducts.find((product) => product.id === productId)?.name;
                const capacity = productThresholds.find((threshold) => threshold.productCapacity)?.productCapacity;
                const fillLevel = fillLevels.find(
                    (productFillLevel) =>
                        productFillLevel.shelterId === shelter.id && productFillLevel.productId === productId
                )?.fillLevel;

                const requiredAction = getRequiredAction(capacity || 0, fillLevel || getShelterFillLevel(shelter));

                shelterProducts.push({
                    name: name || '',
                    requiredAction
                });
            });

            return shelterProducts;
        },
        [fillLevels, rfidTagProducts]
    );

    const getShelterFillLevel = useCallback(
        (shelter: Shelter): number => {
            return fillLevels
                .filter((x) => x.shelterId === shelter.id)
                .reduce((acc, { fillLevel }) => acc + fillLevel, 0);
        },
        [fillLevels]
    );

    const getRequiredAction = useCallback(
        (capacity: number, fillLevel: number): string => {
            if (fillLevel < capacity) {
                return `+ ${capacity - fillLevel}`;
            }
            if (fillLevel > capacity) {
                return `- ${fillLevel - capacity}`;
            }
            return t('none');
        },
        [t]
    );

    const getHealthState = useCallback(
        (shelter: Shelter) => {
            const fillLevel = getShelterFillLevel(shelter);
            const thresholds = shelter?.notificationRule?.thresholds;

            if (!thresholds) {
                console.log(`[TrolleyShelterMonitoring] No thresholds for shelter ${shelter.id}, defaulting to good`);
                return healthStates.good;
            }

            const lowestLowerThreshold = getLowestLowerThreshold(thresholds);
            const highestUpperThreshold = getHighestUpperThreshold(thresholds);
            const highestLowerThreshold = getHighestLowerThreshold(thresholds);
            const lowestUpperThreshold = getLowestUpperThreshold(thresholds);

            if (
                (Number.isFinite(lowestLowerThreshold) && fillLevel <= lowestLowerThreshold) ||
                (Number.isFinite(highestUpperThreshold) && fillLevel >= highestUpperThreshold)
            ) {
                return healthStates.bad;
            }

            if (
                (Number.isFinite(highestLowerThreshold) && fillLevel <= highestLowerThreshold) ||
                (Number.isFinite(lowestUpperThreshold) && fillLevel >= lowestUpperThreshold)
            ) {
                return healthStates.warn;
            }

            return healthStates.good;
        },
        [getShelterFillLevel]
    );

    const getLowestLowerThreshold = (thresholds: Threshold[]) => {
        const lowerThresholds = thresholds.filter((x) => x.thresholdType === ThresholdType.Lower).map((x) => x.value);
        return lowerThresholds.length > 0 ? Math.min(...lowerThresholds) : Infinity;
    };

    const getHighestLowerThreshold = (thresholds: Threshold[]) => {
        const lowerThresholds = thresholds.filter((x) => x.thresholdType === ThresholdType.Lower).map((x) => x.value);
        return lowerThresholds.length > 0 ? Math.max(...lowerThresholds) : -Infinity;
    };

    const getLowestUpperThreshold = (thresholds: Threshold[]) => {
        const upperThresholds = thresholds.filter((x) => x.thresholdType === ThresholdType.Upper).map((x) => x.value);
        return upperThresholds.length > 0 ? Math.min(...upperThresholds) : Infinity;
    };

    const getHighestUpperThreshold = (thresholds: Threshold[]) => {
        const upperThresholds = thresholds.filter((x) => x.thresholdType === ThresholdType.Upper).map((x) => x.value);
        return upperThresholds.length > 0 ? Math.max(...upperThresholds) : Infinity;
    };

    const getStatusColor = useCallback(
        (shelter: Shelter): string => {
            return getHealthState(shelter).color;
        },
        [getHealthState]
    );

    const filteredShelters = React.useMemo(() => {
        // Allow display even if fillLevels is empty - fill level will just be 0
        if (!allShelters.length) {
            return [];
        }

        const filtered = allShelters.filter((shelter) => {
            const healthState = getHealthState(shelter);
            const matches = selectedHealths.includes(healthState.id);
            return matches;
        });

        return filtered;
    }, [allShelters, fillLevels, selectedHealths, getHealthState]);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-cast zmdi-hc-rotate-180" style={{ fontSize: '16px' }} />}
                title={`${t('trolleyShelter')} ${t('monitoring')}`}
                menus={getMenuData()}
                iconSize="18px"
                currentRoute={{ path: '/statistics/rfid/monitoring' }}
                t={t}
                lastUpdate={lastUpdate}
                hasAutoRefresh={true}
            />

            <Box sx={{ px: 3, pb: 3, mt: 1 }}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid
                        item
                        xs={6}
                        md={3}
                        sx={{ flexBasis: { md: '560px' }, maxWidth: { md: '560px' }, flexGrow: { md: 0 } }}
                    >
                        <ShopSelector
                            value={selectedShopIds[0] ?? null}
                            onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                            label={t('shop')}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AllHealthsSelector
                            healthStates={healthStates}
                            selectedOnStart={[healthStates.bad.id]}
                            onSelectedHealthsChanged={setSelectedHealths}
                        />
                    </Grid>
                </Grid>

                {!selectedShopIds[0] && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {loading && selectedShopIds[0] && (
                    <LoadingSpinner showBackground={false} showMessage={true} message={t('loadingData')} size={60} />
                )}

                {!loading && !filteredShelters.length && allShelters.length === 0 && selectedShopIds[0] && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {!loading && !filteredShelters.length && allShelters.length > 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {!loading && filteredShelters.length > 0 && (
                    <Grid container spacing={3}>
                        {filteredShelters.map((shelter) => (
                            <TrolleyShelterMonitoringCard
                                key={shelter.id}
                                shelter={shelter}
                                fillLevel={getShelterFillLevel(shelter)}
                                getStatusColor={getStatusColor}
                                getRequiredAction={getRequiredAction}
                                getShelterProducts={getShelterProducts}
                                types={types}
                            />
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default TrolleyShelterMonitoring;
